import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectUserRole,
} from "../../../../core/data/slice/authSlice";
import { useGetCenterByIdQuery } from "../api/centerApi";
import { CenterResponseType } from "../types/centerResponse";

const CenterProfile = () => {
  const currentUser = useSelector(selectCurrentUser);
  const userRole = useSelector(selectUserRole);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if user is center admin and has center_id
  const hasAccess = userRole === "centeradmin" && currentUser?.center_id;

  // Use RTK Query hook to fetch center data
  const { data, isLoading, error } = useGetCenterByIdQuery(
    { id: String(currentUser?.center_id || "") },
    { skip: !hasAccess }
  );

  const centerData = (data?.data?.center as CenterResponseType) || null;
  const errorMessage = !hasAccess
    ? "You do not have access to view center information"
    : error
    ? "Failed to load center information"
    : null;

  // Format date
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    if (!status) return "secondary";
    switch (status.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Function to toggle accordion sections
  const toggleAccordion = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Center Profile</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Center Management</Link>
                </li>
                <li className="breadcrumb-item active">Center Profile</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="mb-2">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-primary d-flex align-items-center"
              >
                <i className="ti ti-chevron-left me-2"></i>
                Back
              </button>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            {isLoading ? (
              <div className="card">
                <div className="card-body p-0 py-3">
                  <div className="text-center">
                    Loading center information...
                  </div>
                </div>
              </div>
            ) : errorMessage ? (
              <div className="alert alert-danger">{errorMessage}</div>
            ) : centerData ? (
              <>
                {/* Center Details Card */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-building-community fs-16" />
                      </span>
                      <h4 className="text-dark mb-0">Center Information</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped mb-0">
                        <tbody>
                          <tr>
                            <th className="bg-light-subtle">
                              <i className="ti ti-building text-primary me-2"></i>
                              Center Name
                            </th>
                            <td>{centerData.name}</td>
                          </tr>
                          <tr>
                            <th className="bg-light-subtle">
                              <i className="ti ti-barcode text-primary me-2"></i>
                              Center Code
                            </th>
                            <td>{centerData.code}</td>
                          </tr>
                          <tr>
                            <th className="bg-light-subtle">
                              <i className="ti ti-category text-primary me-2"></i>
                              Type
                            </th>
                            <td>{centerData.type}</td>
                          </tr>
                          <tr>
                            <th className="bg-light-subtle">
                              <i className="ti ti-map-pin text-primary me-2"></i>
                              Zone
                            </th>
                            <td>{centerData.zone}</td>
                          </tr>
                          <tr>
                            <th className="bg-light-subtle">
                              <i className="ti ti-map text-primary me-2"></i>
                              District
                            </th>
                            <td>{centerData.district}</td>
                          </tr>
                          <tr>
                            <th className="bg-light-subtle">
                              <i className="ti ti-circle-check text-primary me-2"></i>
                              Status
                            </th>
                            <td>
                              <span
                                className={`badge badge-soft-${getStatusColor(
                                  centerData.status
                                )} d-inline-flex align-items-center`}
                              >
                                <i className="ti ti-circle-filled fs-5 me-1"></i>
                                {centerData.status === "active"
                                  ? "Active"
                                  : "Inactive"}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <th className="bg-light-subtle">
                              <i className="ti ti-calendar text-primary me-2"></i>
                              Created Date
                            </th>
                            <td>{formatDate(centerData.created_at)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Contact Information Card */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-address-book fs-16" />
                      </span>
                      <h5 className="card-title mb-0">Contact Information</h5>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="avatar avatar-sm me-3 flex-shrink-0">
                            <div className="avatar-title bg-primary-light text-primary rounded">
                              <i className="ti ti-phone"></i>
                            </div>
                          </div>
                          <div>
                            <p className="text-muted mb-0">Phone</p>
                            <h6 className="mb-0">
                              <a
                                href={`tel:${centerData.phone}`}
                                className="text-body"
                              >
                                {centerData.phone || "-"}
                              </a>
                            </h6>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="avatar avatar-sm me-3 flex-shrink-0">
                            <div className="avatar-title bg-primary-light text-primary rounded">
                              <i className="ti ti-mail"></i>
                            </div>
                          </div>
                          <div>
                            <p className="text-muted mb-0">Email</p>
                            <h6 className="mb-0">
                              <a
                                href={`mailto:${centerData.email}`}
                                className="text-body"
                              >
                                {centerData.email || "-"}
                              </a>
                            </h6>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-sm me-3 flex-shrink-0">
                            <div className="avatar-title bg-primary-light text-primary rounded">
                              <i className="ti ti-home"></i>
                            </div>
                          </div>
                          <div>
                            <p className="text-muted mb-0">Address</p>
                            <h6 className="mb-0">
                              {centerData.address || "-"}
                            </h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fee Configuration Card */}
                {((centerData?.courseFees &&
                  centerData.courseFees.length > 0) ||
                  (centerData?.regFees && centerData.regFees.length > 0) ||
                  (centerData?.courseFeeShare &&
                    centerData.courseFeeShare.length > 0) ||
                  (centerData?.regFeeShare &&
                    centerData.regFeeShare.length > 0)) && (
                  <div className="card shadow-none border mb-4">
                    <div className="card-header bg-light border-bottom">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                            <i className="ti ti-cash fs-16" />
                          </span>
                          <h5 className="card-title mb-0">Fee Configuration</h5>
                        </div>
                        <span className="badge text-primary rounded-pill">
                          {(centerData?.regFees?.length || 0) +
                            (centerData?.courseFees?.length || 0)}{" "}
                          Fee Type
                          {(centerData?.regFees?.length || 0) +
                            (centerData?.courseFees?.length || 0) >
                          1
                            ? "s"
                            : ""}
                        </span>
                      </div>
                    </div>
                    <div className="card-body">
                      {/* Fee Accordions */}
                      <div className="accordion" id="feeAccordion">
                        {/* Registration Fees Accordion */}
                        {centerData?.regFees &&
                          centerData.regFees.length > 0 && (
                            <div className="accordion-item mb-3">
                              <h2 className="accordion-header">
                                <button
                                  className={`accordion-button ${
                                    expandedSection === "registration"
                                      ? ""
                                      : "collapsed"
                                  }`}
                                  type="button"
                                  onClick={() =>
                                    toggleAccordion("registration")
                                  }
                                >
                                  <div className="d-flex flex-wrap justify-content-between w-100 gap-2">
                                    <div className="d-flex align-items-center">
                                      <div className="avatar avatar-xs me-2 flex-shrink-0">
                                        <div className="avatar-title text-primary rounded">
                                          <i className="ti ti-ticket"></i>
                                        </div>
                                      </div>
                                      <span className="fw-medium">
                                        Registration Fees
                                      </span>
                                    </div>
                                    <span className="badge text-primary">
                                      {centerData.regFees.length} Course
                                      {centerData.regFees.length > 1 ? "s" : ""}
                                    </span>
                                  </div>
                                </button>
                              </h2>
                              <div
                                className={`accordion-collapse collapse ${
                                  expandedSection === "registration"
                                    ? "show"
                                    : ""
                                }`}
                              >
                                <div className="accordion-body p-0">
                                  <div className="table-responsive">
                                    <table className="table table-bordered mb-0">
                                      <thead className="thead-light">
                                        <tr>
                                          <th>Course</th>
                                          <th className="text-end">Amount</th>
                                          {centerData.regFeeShare &&
                                            centerData.regFeeShare.length >
                                              0 && (
                                              <>
                                                <th>Share Type</th>
                                                <th className="text-end">
                                                  Share Amount
                                                </th>
                                                <th className="text-end">
                                                  Center Earnings
                                                </th>
                                              </>
                                            )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {centerData.regFees.map(
                                          (fee: any, index) => {
                                            // Find matching share if exists
                                            const shareInfo =
                                              centerData.regFeeShare?.find(
                                                (share: any) =>
                                                  share.course_id ===
                                                  fee.course_id
                                              );

                                            // Calculate center earnings
                                            let centerEarnings = fee.fee;
                                            if (shareInfo) {
                                              centerEarnings =
                                                shareInfo.share_type ===
                                                "percent"
                                                  ? fee.fee -
                                                    (fee.fee *
                                                      Number(
                                                        shareInfo.share_amount
                                                      )) /
                                                      100
                                                  : fee.fee -
                                                    Number(
                                                      shareInfo.share_amount
                                                    );
                                            }

                                            return (
                                              <tr key={index}>
                                                <td>
                                                  <div className="d-flex align-items-center">
                                                    <span>
                                                      {fee.course_name || "-"}
                                                    </span>
                                                  </div>
                                                </td>
                                                <td className="text-end">
                                                  ₹
                                                  {fee.fee.toLocaleString(
                                                    "en-IN"
                                                  ) || 0}
                                                </td>
                                                {centerData.regFeeShare &&
                                                  centerData.regFeeShare
                                                    .length > 0 && (
                                                    <>
                                                      <td>
                                                        <span
                                                          className={`badge badge-soft-${
                                                            shareInfo?.share_type ===
                                                            "percent"
                                                              ? "info"
                                                              : "success"
                                                          }`}
                                                        >
                                                          {shareInfo?.share_type ===
                                                          "percent"
                                                            ? "Percentage"
                                                            : "Fixed"}
                                                        </span>
                                                      </td>
                                                      <td className="text-end">
                                                        {shareInfo ? (
                                                          <span className="text-danger">
                                                            {shareInfo.share_type ===
                                                            "percent"
                                                              ? `${shareInfo.share_amount}%`
                                                              : `₹${shareInfo.share_amount.toLocaleString(
                                                                  "en-IN"
                                                                )}`}
                                                          </span>
                                                        ) : (
                                                          "-"
                                                        )}
                                                      </td>
                                                      <td className="text-end">
                                                        <span className="text-success">
                                                          ₹
                                                          {centerEarnings.toLocaleString(
                                                            "en-IN"
                                                          )}
                                                        </span>
                                                      </td>
                                                    </>
                                                  )}
                                              </tr>
                                            );
                                          }
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                        {/* Course Fees Accordions - One for each course */}
                        {centerData?.courseFees &&
                          centerData.courseFees.map((courseFee: any, idx) => {
                            // Find course share info
                            const courseShare = centerData.courseFeeShare?.find(
                              (share: any) =>
                                share.course_id === courseFee.course_id
                            );

                            return (
                              <div key={idx} className="accordion-item mb-3">
                                <h2 className="accordion-header">
                                  <button
                                    className={`accordion-button ${
                                      expandedSection ===
                                      `course-${courseFee.id}`
                                        ? ""
                                        : "collapsed"
                                    }`}
                                    type="button"
                                    onClick={() =>
                                      toggleAccordion(`course-${courseFee.id}`)
                                    }
                                  >
                                    <div className="d-flex flex-wrap justify-content-between w-100 gap-2">
                                      <div className="d-flex align-items-center">
                                        <div className="avatar avatar-xs me-2 flex-shrink-0">
                                          <div className="avatar-title text-primary rounded">
                                            <i className="ti ti-book"></i>
                                          </div>
                                        </div>
                                        <span className="fw-medium">
                                          Course Fee -{" "}
                                          {courseFee.course_name || ""}
                                        </span>
                                      </div>
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-info-subtle text-info">
                                          {courseShare?.share_type === "percent"
                                            ? "Percentage Share"
                                            : "Fixed Share"}
                                        </span>
                                        <span className="badge bg-primary rounded-pill">
                                          {courseFee.stageFees?.length || 0}{" "}
                                          stages
                                        </span>
                                      </div>
                                    </div>
                                  </button>
                                </h2>
                                <div
                                  className={`accordion-collapse collapse ${
                                    expandedSection === `course-${courseFee.id}`
                                      ? "show"
                                      : ""
                                  }`}
                                >
                                  <div className="accordion-body p-0">
                                    <div className="table-responsive">
                                      <table className="table table-bordered mb-0">
                                        <thead className="thead-light">
                                          <tr>
                                            <th>#</th>
                                            <th>Stage</th>
                                            <th className="text-end">
                                              Fee Amount
                                            </th>
                                            {courseShare && (
                                              <>
                                                <th className="text-end">
                                                  Share
                                                </th>
                                                <th className="text-end">
                                                  Share Value
                                                </th>
                                                <th className="text-end">
                                                  Center Earnings
                                                </th>
                                              </>
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {courseFee.stageFees.map(
                                            (
                                              stageFee: any,
                                              stageIdx: number
                                            ) => {
                                              // Find matching stage share
                                              const stageShare =
                                                courseShare?.stageFeeShare?.find(
                                                  (share: any) =>
                                                    share.stage_id ===
                                                    stageFee.stage_id
                                                );

                                              // Calculate center earnings
                                              let shareValue = 0;
                                              let centerEarnings =
                                                stageFee.amount;

                                              if (stageShare && courseShare) {
                                                if (
                                                  courseShare.share_type ===
                                                  "percent"
                                                ) {
                                                  shareValue =
                                                    (stageFee.amount *
                                                      Number(
                                                        stageShare.share_amount
                                                      )) /
                                                    100;
                                                } else {
                                                  shareValue = Number(
                                                    stageShare.share_amount
                                                  );
                                                }
                                                centerEarnings =
                                                  stageFee.amount - shareValue;
                                              }

                                              return (
                                                <tr key={stageIdx}>
                                                  <td>{stageIdx + 1}</td>
                                                  <td>
                                                    <div className="d-flex align-items-center">
                                                      <span>
                                                        {stageFee.stage_name ||
                                                          ""}
                                                      </span>
                                                    </div>
                                                  </td>
                                                  <td className="text-end">
                                                    ₹
                                                    {stageFee.amount.toLocaleString(
                                                      "en-IN"
                                                    ) || 0}
                                                  </td>
                                                  {courseShare && (
                                                    <>
                                                      <td className="text-end">
                                                        {stageShare ? (
                                                          <span>
                                                            {courseShare.share_type ===
                                                            "percent"
                                                              ? `${stageShare.share_amount}%`
                                                              : `₹${stageShare.share_amount.toLocaleString(
                                                                  "en-IN"
                                                                )}`}
                                                          </span>
                                                        ) : (
                                                          "-"
                                                        )}
                                                      </td>
                                                      <td className="text-end">
                                                        <span className="text-danger">
                                                          ₹
                                                          {shareValue.toLocaleString(
                                                            "en-IN"
                                                          )}
                                                        </span>
                                                      </td>
                                                      <td className="text-end">
                                                        <span className="text-success">
                                                          ₹
                                                          {centerEarnings.toLocaleString(
                                                            "en-IN"
                                                          )}
                                                        </span>
                                                      </td>
                                                    </>
                                                  )}
                                                </tr>
                                              );
                                            }
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="alert alert-warning">
                No center information available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CenterProfile;
